import CheckCircleOutlined from '@mui/icons-material/CheckCircleOutlined';
import RadioButtonUncheckedOutlined from '@mui/icons-material/RadioButtonUncheckedOutlined';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React from 'react';

export interface SetupStep {
  label: string;
  completed: boolean;
}

interface Props {
  steps: [SetupStep, ...SetupStep[]];
}

const SetupIndicator: React.FC<Props> = ({ steps }) => {
  const { translation } = useTranslation();
  const total = steps.length;
  const completedCount = steps.filter((step) => step.completed).length;
  const isComplete = completedCount === total;

  const tooltipContent = (
    <Stack spacing={1}>
      <Typography
        variant="micro"
        sx={(theme) => ({ color: theme.palette.secondary.contrastText, textTransform: 'uppercase' })}
      >
        {translation.setup_steps}
      </Typography>
      <Stack spacing={0.5}>
        {steps.map((step) => (
          <Stack key={step.label} direction="row" alignItems="center" spacing={1}>
            {step.completed ? (
              <CheckCircleOutlined
                fontSize="extraSmall"
                sx={(theme) => ({ color: theme.palette.secondary.contrastText })}
              />
            ) : (
              <RadioButtonUncheckedOutlined
                fontSize="extraSmall"
                sx={(theme) => ({ color: theme.palette.secondary.contrastText })}
              />
            )}
            <Typography
              variant="caption"
              sx={(theme) => ({ color: theme.palette.secondary.contrastText, whiteSpace: 'nowrap' })}
            >
              {step.label}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );

  return (
    <Tooltip
      title={tooltipContent}
      placement="bottom-start"
      arrow
      slotProps={{
        tooltip: { sx: (theme) => ({ bgcolor: theme.palette.secondary.main, px: 1, py: 0.5 }) },
        arrow: { sx: (theme) => ({ color: theme.palette.secondary.main }) }
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} sx={{ cursor: 'default' }}>
        <Typography variant="micro" color="textSecondary" sx={{ textTransform: 'uppercase', whiteSpace: 'nowrap' }}>
          {translation.setup}
        </Typography>
        <Box
          sx={(theme) => ({
            width: 60,
            height: 4,
            borderRadius: 0.5,
            bgcolor: theme.palette.secondary.main,
            position: 'relative',
            flexShrink: 0
          })}
        >
          {completedCount > 0 && (
            <Box
              sx={(theme) => ({
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: `${(completedCount / total) * 100}%`,
                borderRadius: 0.5,
                bgcolor: isComplete ? theme.palette.success.main : theme.palette.warning.main
              })}
            />
          )}
        </Box>
        <Typography variant="micro" color={isComplete ? 'success' : 'textSecondary'} sx={{ whiteSpace: 'nowrap' }}>
          {`${completedCount}/${total}`}
        </Typography>
      </Stack>
    </Tooltip>
  );
};

export default SetupIndicator;
